import { useCallback, useEffect, useRef, useState } from 'react';
import { useService } from '../../../../contexts/ServiceContext';
import { ListingResource, MediaField } from '../../../../system/field';
import { useResoResource } from '../../../../reso/useResoResource';
import { MediaResource } from '../../../../reso/resources/MediaResource';

export interface UseAIOptions {
  maxRetries?: number;
  initialDelay?: number;
  backoffFactor?: number;
  maxDescriptionLength?: number;
  listingKey: string;
  batchSize?: number;
  onComplete?: () => void; // Callback for when all processing is complete
}

export interface UseAIResult {
  generateImageDescription: (
    mediaKey: string,
    imageUrl: string,
  ) => Promise<string | null>;
  generateBatchDescriptions: (
    mediaItems: Array<{ key: string; url: string }>,
  ) => Promise<void>;
  isGeneratingDescription: (mediaKey: string) => boolean;
  isPartOfCurrentBatchGenerating: (mediaKey: string) => boolean;
  cancelGeneration: (mediaKey: string) => void;
  pendingGenerations: string[];
  isProcessingBatch: boolean;
}

export function useAI(options: UseAIOptions): UseAIResult {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    backoffFactor = 2,
    maxDescriptionLength = 40,
    listingKey: providedListingKey,
    batchSize = 5,
    onComplete,
  } = options;

  const { generateAIDescriptionForImage } = useService();
  const { resourceManager: mediaManager } = useResoResource<MediaResource>(
    ListingResource.Media,
  );

  // Track which media items are currently having descriptions generated
  const [generatingMedia, setGeneratingMedia] = useState<
    Record<string, boolean>
  >({});
  const [pendingGenerations, setPendingGenerations] = useState<string[]>([]);
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);

  // Track abort controllers for each media item
  const abortControllers = useRef<Record<string, AbortController>>({});
  // Track batch queue
  const batchQueue = useRef<Array<Array<{ key: string; url: string }>>>([]);
  // Track all keys in the batch queue
  const allQueuedKeys = useRef<Set<string>>(new Set());
  // Track if we're in a batch to handle callback properly
  const isInBatchRef = useRef<boolean>(false);
  // Track all keys that were part of the current batch (including completed ones)
  const currentBatchKeys = useRef<Set<string>>(new Set());
  // Track if the batch has completed (onComplete was called)
  const batchCompletedRef = useRef<boolean>(false);
  // Track if the onComplete callback has been scheduled
  const onCompleteScheduledRef = useRef<boolean>(false);

  // Cancel a specific generation
  const cancelGeneration = useCallback((mediaKey: string) => {
    const controller = abortControllers.current[mediaKey];
    if (controller) {
      controller.abort();
      delete abortControllers.current[mediaKey];

      setGeneratingMedia((prev) => {
        const updated = { ...prev };
        delete updated[mediaKey];
        return updated;
      });

      setPendingGenerations((prev) => prev.filter((key) => key !== mediaKey));

      // Remove from all queued keys
      allQueuedKeys.current.delete(mediaKey);

      // Note: We don't remove from currentBatchKeys because we want to track
      // all items that were part of this batch, even if they're cancelled
    }
  }, []);

  // Clean up abort controllers on unmount
  useEffect(() => {
    return () => {
      Object.values(abortControllers.current).forEach((controller) => {
        if (controller) controller.abort();
      });
    };
  }, []);

  // Update the media with the new description
  const updateMediaWithDescription = useCallback(
    async (mediaKey: string, description: string): Promise<void> => {
      try {
        // Get the media item by key
        const cwa = mediaManager.cwa();
        const mediaItem = await cwa.getById(mediaKey);

        if (!mediaItem) {
          throw new Error(`Media item with key ${mediaKey} not found`);
        }

        // Create an updated version of the media resource
        const updatedMedia = new MediaResource({
          ...mediaItem,
          [MediaField.MEDIA_SHORT_DESCRIPTION]: description,
        });

        // Update directly through RESO API
        await cwa.update(updatedMedia);

        // No need to update listing context as the RESO API update will trigger
        // context refresh via other mechanisms
      } catch (error) {
        console.error('Failed to update media description:', error);
        throw error;
      } finally {
        // Remove from queued keys when processing is complete
        allQueuedKeys.current.delete(mediaKey);

        // Note: We don't remove from currentBatchKeys because we want to track
        // all items that were part of this batch, even if they're completed

        // Check if we should call onComplete (all keys processed and we're in batch mode)
        checkAndTriggerOnComplete();
      }
    },
    [mediaManager],
  );

  // Helper to check if we should trigger onComplete
  const checkAndTriggerOnComplete = useCallback(() => {
    if (
      isInBatchRef.current &&
      allQueuedKeys.current.size === 0 &&
      batchQueue.current.length === 0 &&
      !isProcessingBatch &&
      !batchCompletedRef.current &&
      !onCompleteScheduledRef.current &&
      onComplete
    ) {
      // Use a ref to track that we've scheduled the callback
      onCompleteScheduledRef.current = true;

      // Add a small delay to ensure all updates have settled
      setTimeout(() => {
        // Mark batch as complete
        batchCompletedRef.current = true;
        isInBatchRef.current = false;
        onCompleteScheduledRef.current = false;

        // Call onComplete
        onComplete();
      }, 500);
    }
  }, [isProcessingBatch, onComplete]);

  // Generate description with retries
  const generateImageDescription = useCallback(
    async (mediaKey: string, imageUrl: string): Promise<string | null> => {
      // Use provided listing key or get from context
      const listingKey = providedListingKey;

      if (!listingKey || !mediaKey || !imageUrl) {
        return null;
      }

      // Create abort controller
      const controller = new AbortController();
      abortControllers.current[mediaKey] = controller;

      // Set generating state
      setGeneratingMedia((prev) => ({
        ...prev,
        [mediaKey]: true,
      }));

      setPendingGenerations((prev) => [...prev, mediaKey]);

      try {
        let retries = 0;
        let delay = initialDelay;
        let lastError = null;

        while (retries <= maxRetries) {
          try {
            // Check if generation was aborted
            if (controller.signal.aborted) {
              throw new Error('Description generation aborted');
            }

            // Generate description
            const result = await generateAIDescriptionForImage(
              listingKey,
              imageUrl,
              maxDescriptionLength,
            );

            // Truncate description if needed
            let description = result.description;
            if (description.length > maxDescriptionLength) {
              description =
                description.substring(0, maxDescriptionLength - 3) + '...';
            }

            // Update the media resource with the new description
            await updateMediaWithDescription(mediaKey, description);

            // Return the description
            return description;
          } catch (error) {
            lastError = error;

            // Stop retrying if aborted
            if (controller.signal.aborted) {
              throw error;
            }

            // Retry with exponential backoff
            if (retries < maxRetries) {
              await new Promise((resolve) => setTimeout(resolve, delay));
              delay *= backoffFactor;
              retries++;
            } else {
              throw error;
            }
          }
        }

        throw lastError;
      } catch (error) {
        console.error('Failed to generate image description:', error);
        return null;
      } finally {
        // Clear generating state
        setGeneratingMedia((prev) => {
          const updated = { ...prev };
          delete updated[mediaKey];
          return updated;
        });

        setPendingGenerations((prev) => prev.filter((key) => key !== mediaKey));

        // Clear abort controller
        delete abortControllers.current[mediaKey];

        // Check if we should call onComplete
        checkAndTriggerOnComplete();
      }
    },
    [
      providedListingKey,
      generateAIDescriptionForImage,
      updateMediaWithDescription,
      initialDelay,
      maxRetries,
      backoffFactor,
      maxDescriptionLength,
      checkAndTriggerOnComplete,
    ],
  );

  // Process the next batch in the queue
  const processNextBatch = useCallback(async (): Promise<any> => {
    if (batchQueue.current.length === 0) {
      setIsProcessingBatch(false);

      // Check if we need to call onComplete
      checkAndTriggerOnComplete();
      return;
    }

    setIsProcessingBatch(true);
    const currentBatch = batchQueue.current.shift();

    if (!currentBatch || currentBatch.length === 0) {
      return processNextBatch();
    }

    try {
      // Process all items in the current batch concurrently
      await Promise.all(
        currentBatch.map(({ key, url }) => generateImageDescription(key, url)),
      );
    } catch (error) {
      console.error('Error processing batch:', error);
    } finally {
      // Process the next batch regardless of success/failure
      setTimeout(() => processNextBatch(), 500); // Small delay between batches
    }
  }, [generateImageDescription, checkAndTriggerOnComplete]);

  // Add a batch of media items to the queue for description generation
  const generateBatchDescriptions = useCallback(
    async (mediaItems: Array<{ key: string; url: string }>) => {
      if (mediaItems.length === 0) {
        // If no items and we're not already processing, call onComplete
        if (!isProcessingBatch && !isInBatchRef.current && onComplete) {
          batchCompletedRef.current = true;
          onComplete();
        }
        return;
      }

      const newItems: Array<{ key: string; url: string }> = [];

      // Filter out items that are already in queue/processing
      for (const item of mediaItems) {
        if (!allQueuedKeys.current.has(item.key)) {
          newItems.push(item);
        }
      }

      // If no new items after filtering, just return
      if (newItems.length === 0) {
        return;
      }

      // If we're not already in batch mode, set it up
      if (!isInBatchRef.current) {
        isInBatchRef.current = true;
        batchCompletedRef.current = false;
        onCompleteScheduledRef.current = false;
      }

      // Split new items into batches of the specified size
      const newBatches: Array<Array<{ key: string; url: string }>> = [];
      for (let i = 0; i < newItems.length; i += batchSize) {
        newBatches.push(newItems.slice(i, i + batchSize));
      }

      // Add all new keys to tracking
      newItems.forEach((item) => {
        allQueuedKeys.current.add(item.key);
        currentBatchKeys.current.add(item.key);
      });

      // Add new batches to the queue
      batchQueue.current.push(...newBatches);

      // Start processing if not already in progress
      if (!isProcessingBatch) {
        processNextBatch();
      }
    },
    [batchSize, isProcessingBatch, processNextBatch, onComplete],
  );

  // Check if a description is being generated for a media item
  const isGeneratingDescription = useCallback(
    (mediaKey: string): boolean => {
      // Return true if either actively generating or in the queue
      return !!generatingMedia[mediaKey] || allQueuedKeys.current.has(mediaKey);
    },
    [generatingMedia],
  );

  // Check if a media key is part of the current batch and batch is still processing
  const isPartOfCurrentBatchGenerating = useCallback(
    (mediaKey: string): boolean => {
      // If batch is completed, return false
      if (batchCompletedRef.current) {
        return false;
      }

      // If the media key is part of current batch and we're still in batch mode
      return isInBatchRef.current && currentBatchKeys.current.has(mediaKey);
    },
    [],
  );

  return {
    generateImageDescription,
    generateBatchDescriptions,
    isGeneratingDescription,
    isPartOfCurrentBatchGenerating,
    cancelGeneration,
    pendingGenerations,
    isProcessingBatch,
  };
}
